import GroupIcon from '@mui/icons-material/Group'
import PersonIcon from '@mui/icons-material/Person'
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { AppBarR, Wrapper } from '../components/common'
import OfficeList from '../components/profile/OfficeList'
import UserInfo from '../components/profile/UserInfo'
import db from '../db/db'
import { sessionState } from '../state'
import Database from '../types/Database'
import { addRolesToOffices, OfficeWithRole } from '../utils/profileHelpers'

import routes from './routes'

/**
 * Profile - User profile page component
 *
 * Displays user profile information and office memberships.
 * Shows personal details, theme preferences, and office roles.
 *
 * NOTE: Profile editing functionality is not yet implemented.
 * When profile editing is added, track "profile_updated" event with fields_updated parameter.
 * See Requirements 8.1, 8.2, 8.3, 8.4 in google-analytics-integration spec.
 */
const Profile: React.FC = () => {
  const session = useRecoilValue(sessionState)
  const { user, offices, isAuthenticating } = session
  const navigate = useNavigate()

  const [officesWithRoles, setOfficesWithRoles] = useState<OfficeWithRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch office managers and determine roles
  useEffect(() => {
    const fetchOfficeRoles = async () => {
      if (!user || !offices) {
        setLoading(false)
        return
      }

      // Check if user has a valid email for manager lookups
      if (
        !user.email ||
        typeof user.email !== 'string' ||
        user.email.trim() === ''
      ) {
        console.warn('User does not have a valid email address:', user.email)
        // Still proceed with role determination, but user will be treated as member/owner only
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch office managers for role determination
        // Get all office managers for the offices the user has access to
        const officeIds = offices.map(office => office.oid).filter(Boolean)
        console.log('Fetching office managers for office IDs:', officeIds)
        console.log('Current user email:', user.email)
        let officeManagers: Database.OfficeManager[] = []

        if (officeIds.length > 0) {
          try {
            // Fetch all office managers for these offices
            officeManagers = await db.query({
              collection: 'officeManagers',
              where: ['officeId', 'in', officeIds]
            })
            console.log('Successfully fetched office managers:', officeManagers)

            // Also try to fetch office managers specifically for this user's email
            // Only if user has a valid email address
            if (
              user.email &&
              typeof user.email === 'string' &&
              user.email.trim() !== ''
            ) {
              try {
                const userSpecificManagers = await db.query({
                  collection: 'officeManagers',
                  where: ['email', '==', user.email]
                })
                console.log(
                  'User-specific office managers:',
                  userSpecificManagers
                )

                // Merge the results, avoiding duplicates
                const allManagers = [...officeManagers, ...userSpecificManagers]
                officeManagers = allManagers.filter(
                  (manager, index, self) =>
                    index === self.findIndex(m => m.id === manager.id)
                )
                console.log('Final merged office managers:', officeManagers)
              } catch (emailQueryError) {
                console.warn(
                  'Could not fetch user-specific office managers:',
                  emailQueryError
                )
                // Continue with the office managers we already fetched
              }
            } else {
              console.warn(
                'User email is not available for office manager lookup:',
                user.email
              )
            }
          } catch (queryError) {
            console.warn(
              'Could not fetch office managers, using empty array:',
              queryError
            )
            // Continue with empty array - user will be treated as member in all offices
            officeManagers = []
          }
        }

        // Add roles to offices
        const officesWithRoleData = addRolesToOffices(
          offices,
          user,
          officeManagers
        )
        setOfficesWithRoles(officesWithRoleData)
      } catch (err) {
        console.error('Error fetching office roles:', err)
        setError(
          'Failed to load office information. Please try refreshing the page.'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchOfficeRoles()
  }, [user, offices])

  // Redirect unauthenticated users to sign-in page
  if (!isAuthenticating && !user) {
    return <Navigate to={routes.signin} replace />
  }

  // Show loading state during authentication
  if (isAuthenticating) {
    return (
      <>
        <AppBarR />
        <Wrapper>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight='200px'
          >
            <CircularProgress />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Loading profile...
            </Typography>
          </Box>
        </Wrapper>
      </>
    )
  }

  return (
    <>
      <AppBarR />
      <Wrapper>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <PersonIcon fontSize='small' />
            </Avatar>
            <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
              Profile
            </Typography>
          </Box>

          <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
            Manage your account and office memberships
          </Typography>

          {/* User Information Section */}
          <UserInfo />

          {/* Team Management Navigation */}
          <Box sx={{ mt: 3, mb: 3 }}>
            <Button
              variant='outlined'
              startIcon={<GroupIcon />}
              onClick={() => navigate(routes.myTeam)}
              fullWidth
            >
              My Team
            </Button>
          </Box>

          {/* Office List Section */}
          {error ? (
            <Alert severity='error' sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : (
            <OfficeList offices={officesWithRoles} loading={loading} />
          )}
        </Box>
      </Wrapper>
    </>
  )
}

export default Profile
